import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { odataFetch } from "../../lib/odata-fetch";
import {
  UADMSentenceNonConformanceV3_12Params,
  UADMSentenceNonConformanceV3_1Response,
} from "../../odata/appu4dm/AppU4DMModel";
import { QUADMSentenceNonConformanceV3_1 } from "../../odata/appu4dm/QAppU4DM";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mtuKeys } from "../mtu/mtu-keys";
import { useWorkOrderOperationId } from "../work-order/use-work-order-operation-id";
import { taskKeys } from "../tasks/task-keys";
import { useMachine } from "../machine/use-machine";
import { useWorkOrder } from "../work-order/use-work-order";
import { useWorkOrderOperation } from "../work-order/use-work-order-operation";
import { workOrderKeys } from "../work-order/work-order-keys";

export function useSentenceNC(props?: {
  onSuccess?: VoidFunction;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  const { token } = queryClient.getDefaultOptions().queries?.meta ?? {};
  const { workOrderOperationId } = useWorkOrderOperationId();
  const { machine } = useMachine();
  const { workOrderOperation } = useWorkOrderOperation();
  const { workOrder } = useWorkOrder();

  const { mutate, ...mutation } = useMutation({
    mutationFn: (payload: Payload) => sentenceNC(payload, token!),
    ...props,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: mtuKeys.get(workOrderOperationId?.WorkOrderOperation_Id),
        }),
        queryClient.invalidateQueries({
          queryKey: taskKeys.listAll(
            machine?.EquipmentNId,
            workOrderOperation?.NId,
            workOrder?.NId,
            workOrder?.FinalMaterial.Material.UId
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: workOrderKeys.getOperationId(machine?.EquipmentNId),
        }),
      ]);

      props?.onSuccess?.();
    },
  });

  return {
    sentenceNC: mutate,
    ...mutation,
  };
}

type Payload = {
  ncNId: string;
  statusNId: string;
  note: string;
  reworkId?: string;
};

async function sentenceNC(
  { ncNId, note, statusNId, reworkId }: Payload,
  token: string
) {
  const builder = createQueryBuilderV4(
    "UADMSentenceNonConformanceV3_1",
    new QUADMSentenceNonConformanceV3_1()
  );

  return odataFetch<
    UADMSentenceNonConformanceV3_1Response,
    UADMSentenceNonConformanceV3_12Params
  >({
    builder,
    token,
    method: "POST",
    body: {
      command: {
        NonConformanceNId: ncNId,
        NonConformanceStatusNId: statusNId,
        Notes: note,
        ReworkProcessId: reworkId || null,
        DocumentIds: [],
        EquipmentNIdList: [],
        IsAutomaticallyInvoked: null,
        NonConformancePartialWOOpQuantities: null,
      },
    },
  });
}
